/**
 * Utilization dashboard — DAU / WAU / MAU and the engagement behind them.
 *
 * Mounted in BOTH consoles: the portal admin console passes nothing and gets
 * the default `api` client, the super admin console passes `superAdminApi`.
 * That is why this file must never import `api` and use it directly — a super
 * admin request sent with the portal token would 401 and throw them out to the
 * portal sign-in page. See lib/api.ts's ApiClient docstring.
 *
 * Org scoping is NOT done here. The server resolves the caller to an org and
 * every number arrives already scoped, so a tenant's admin gets their own
 * organization's usage from the same component without a prop saying so.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  Gauge,
  RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { EmptyPanel, PageHero, PageShell } from "@/components/interior-page";
import {
  AreaTrendChart,
  BarChart,
  BucketBar,
  CAT,
  ChartFrame,
  CohortGrid,
  ORD,
  FilterBar,
  RangePicker,
  RankedBars,
  StatTile,
  useAgo,
} from "@/components/charts";
import { api, type ApiClient } from "@/lib/api";

interface SeriesPoint {
  date: string;
  dau: number;
  wau: number;
  mau: number;
  new_active: number;
  returning_active: number;
  signups: number;
}

interface Overview {
  generated_at: string;
  range: { start: string; end: string; days: number };
  headline: {
    dau: number; wau: number; mau: number;
    prev_dau: number; prev_wau: number; prev_mau: number;
    stickiness: number; wau_mau: number;
  };
  series: SeriesPoint[];
  reach: { total_users: number; active_users: number; reach_pct: number };
  recency: {
    today: number; last_7: number; days_8_30: number;
    days_31_90: number; dormant: number; never: number;
  };
  depth: {
    one_day: number; two_to_four: number; five_to_nine: number;
    ten_to_nineteen: number; twenty_plus: number; avg_active_days: number;
  };
  by_role: { key: string; active: number }[];
  by_tier: { key: string; active: number }[];
  by_org: { org_id: number; key: string; active: number }[];
  weekday: { weekday: number; avg_dau: number }[];
  top_users: {
    user_id: string; name: string; email: string; org_name: string | null;
    role: string | null; tier_name: string | null;
    active_days: number; hits: number; last_seen: string | null;
  }[];
  history_starts_on: string | null;
}

interface Retention {
  weeks: number;
  cohorts: { cohort_week: string; size: number; cells: { week: number; pct: number; retained: number }[] }[];
}

interface AdvisorRow {
  user_id: string;
  name: string;
  email: string;
  org_name: string | null;
  active_days: number;
  last_seen: string | null;
  listings_managed: number;
  active_listings: number;
  sold_listings: number;
  interests_received: number;
  replies_sent: number;
  avg_first_reply_hours: number | null;
  replied_threads: number;
  awaiting_reply: number;
  deals_created: number;
  shares_sent: number;
  buyers_referred: number;
}

interface AdvisorProductivity {
  range: { start: string; end: string };
  totals: {
    advisors: number; active_advisors: number; listings_managed: number;
    unassigned_advisors: number; interests_received: number; replies_sent: number;
    awaiting_reply: number; deals_created: number; shares_sent: number;
    avg_first_reply_hours: number | null;
  };
  advisors: AdvisorRow[];
}

/** Which audience the page is reporting on. Drives the API segment and the copy. */
export type Segment = "buyer" | "bl_advisor";

const SEGMENT_COPY: Record<Segment, { eyebrow: string; title: string; accent: string; description: string; noun: string }> = {
  buyer: {
    eyebrow: "Buyer Utilization",
    title: "Who is actually using the platform,",
    accent: "day by day.",
    description:
      "Daily, weekly and monthly active buyers on one axis, plus how deeply they engage and how much of the registered base you are reaching. Windows are rolling and measured in UTC.",
    noun: "buyers",
  },
  bl_advisor: {
    eyebrow: "BL Advisor Utilization",
    title: "Is the advisor desk",
    accent: "actually being worked?",
    description:
      "Advisor sign-in activity alongside the work itself — inventory carried, buyer demand it drew, replies sent, and what is still sitting unanswered. Signing in is not the job, so both halves are shown.",
    noun: "advisors",
  },
};

const RANGES = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
  { value: 180, label: "6m" },
  { value: 365, label: "1y" },
];

const ROLE_LABELS: Record<string, string> = {
  buyer: "Buyers",
  seller: "Sellers",
  bl_advisor: "BL Advisors",
  admin: "Platform Admins",
  org_admin: "Org Admins",
};

const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

const longDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

export default function UtilizationDashboardPage({
  client = api,
  segment = "buyer",
}: { client?: ApiClient; segment?: Segment } = {}) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Overview | null>(null);
  const [retention, setRetention] = useState<Retention | null>(null);
  const [advisors, setAdvisors] = useState<AdvisorProductivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (opts?.silent) setRefreshing(true);
      else setLoading(true);
      try {
        const [overview, cohorts, desk] = await Promise.all([
          client.get<Overview>(`/analytics/utilization?days=${days}&segment=${segment}`),
          client.get<Retention>(`/analytics/utilization/retention?weeks=8&segment=${segment}`),
          // Only the advisor view needs the desk metrics, so the buyer view
          // never pays for the query.
          segment === "bl_advisor"
            ? client.get<AdvisorProductivity>(`/analytics/utilization/advisors?days=${days}`)
            : Promise.resolve(null),
        ]);
        setData(overview);
        setRetention(cohorts);
        setAdvisors(desk);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load utilization data");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [client, days, segment],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const ago = useAgo(data?.generated_at);

  // Series colours come from the ORDINAL ramp, not the categorical slots: the
  // three windows are nested (every daily active is also weekly and monthly
  // active), so the order is real and belongs in the colour. Largest first so
  // MAU sits at the back and DAU reads on top.
  const trendSeries = useMemo(
    () => [
      { key: "mau", label: "MAU · 30-day", color: ORD[0] },
      { key: "wau", label: "WAU · 7-day", color: ORD[2] },
      { key: "dau", label: "DAU · daily", color: ORD[4] },
    ],
    [],
  );

  const mixSeries = useMemo(
    () => [
      { key: "returning_active", label: "Returning", color: CAT[0] },
      { key: "new_active", label: "First time ever", color: CAT[1] },
    ],
    [],
  );

  if (loading) return <Loader className="min-h-[60vh]" />;

  if (error || !data) {
    return (
      <PageShell>
        <EmptyPanel
          icon={AlertTriangle}
          title="Utilization data unavailable"
          description={error || "The usage summary could not be loaded right now."}
        />
      </PageShell>
    );
  }

  const copy = SEGMENT_COPY[segment];
  const isAdvisorView = segment === "bl_advisor";
  const h = data.headline;
  const sparkDau = data.series.map((s) => s.dau);

  const recencyBuckets = [
    { label: "Active today", value: data.recency.today },
    { label: "Last 7 days", value: data.recency.last_7 },
    { label: "8–30 days", value: data.recency.days_8_30 },
    { label: "31–90 days", value: data.recency.days_31_90 },
    { label: "90+ days", value: data.recency.dormant },
    { label: "Never active", value: data.recency.never },
  ];

  const depthBuckets = [
    { label: "1 day", value: data.depth.one_day },
    { label: "2–4 days", value: data.depth.two_to_four },
    { label: "5–9 days", value: data.depth.five_to_nine },
    { label: "10–19 days", value: data.depth.ten_to_nineteen },
    { label: "20+ days", value: data.depth.twenty_plus },
  ];

  const weekdayRows = data.weekday.map((w) => ({
    label: DOW[w.weekday] ?? String(w.weekday),
    value: w.avg_dau,
  }));

  return (
    <PageShell>
      <PageHero
        eyebrow={copy.eyebrow}
        icon={Activity}
        title={copy.title}
        titleAccent={copy.accent}
        description={copy.description}
        aside={
          <>
            <div className="text-base font-bold uppercase tracking-[0.18em] text-primary/80">
              Right now
            </div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">
              {h.dau.toLocaleString()} active today
            </div>
            <p className="mt-2 text-base leading-7 text-muted-foreground">
              {h.wau.toLocaleString()} in the last 7 days and {h.mau.toLocaleString()} in the
              last 30 — {data.reach.reach_pct}% of {data.reach.total_users.toLocaleString()}{" "}
              registered {copy.noun}.
            </p>
          </>
        }
      />

      {/* Controls live here, NOT on the hero banner — see FilterBar. */}
      <FilterBar
        meta={
          <>
            {ago && <span>Updated {ago}</span>}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => void load({ silent: true })}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              Refresh
            </Button>
          </>
        }
      >
        <RangePicker options={RANGES} value={days} onChange={setDays} label="Range" />
      </FilterBar>

      {data.history_starts_on && (
        <p className="rounded-xl border border-border/70 bg-muted/30 px-4 py-3 text-base leading-6 text-muted-foreground">
          <CalendarDays className="mr-2 inline h-4 w-4" />
          Activity history begins {longDate(data.history_starts_on)}. Earlier dates read low
          because nothing was recorded then, not because usage dropped — day-by-day tracking
          starts when the rollup was installed.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatTile
          label="DAU"
          value={h.dau.toLocaleString()}
          tone="gold"
          delta={{ current: h.dau, previous: h.prev_dau }}
          deltaLabel="vs yesterday"
          hint="Active today"
          spark={sparkDau}
        />
        <StatTile
          label="WAU"
          value={h.wau.toLocaleString()}
          delta={{ current: h.wau, previous: h.prev_wau }}
          deltaLabel="vs prior 7 days"
          hint="Rolling 7-day"
        />
        <StatTile
          label="MAU"
          value={h.mau.toLocaleString()}
          delta={{ current: h.mau, previous: h.prev_mau }}
          deltaLabel="vs prior 30 days"
          hint="Rolling 30-day"
        />
        <StatTile
          label="Stickiness"
          value={`${h.stickiness}%`}
          hint="DAU ÷ MAU — how much of the monthly audience shows up daily"
        />
        <StatTile
          label="Reach"
          value={`${data.reach.reach_pct}%`}
          hint={`${data.reach.active_users.toLocaleString()} of ${data.reach.total_users.toLocaleString()} accounts active in 30 days`}
        />
      </div>

      {/* The other half of advisor utilization. The tiles above say who signed
          in; these say whether the desk was worked. */}
      {isAdvisorView && advisors && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatTile
            label="Advisors"
            value={advisors.totals.advisors.toLocaleString()}
            tone="gold"
            hint={`${advisors.totals.active_advisors} signed in during this range`}
          />
          <StatTile
            label="Listings managed"
            value={advisors.totals.listings_managed.toLocaleString()}
            hint={
              advisors.totals.unassigned_advisors > 0
                ? `${advisors.totals.unassigned_advisors} advisor${advisors.totals.unassigned_advisors === 1 ? "" : "s"} carrying none`
                : "Every advisor carries inventory"
            }
          />
          <StatTile
            label="Interests received"
            value={advisors.totals.interests_received.toLocaleString()}
            hint="Buyer enquiries on advisor-managed listings"
          />
          <StatTile
            label="Awaiting reply"
            value={advisors.totals.awaiting_reply.toLocaleString()}
            invertDelta
            hint="Threads where the buyer spoke last — the live backlog"
          />
          <StatTile
            label="Avg first reply"
            value={
              advisors.totals.avg_first_reply_hours != null
                ? `${advisors.totals.avg_first_reply_hours}h`
                : "—"
            }
            hint={
              advisors.totals.avg_first_reply_hours != null
                ? "From enquiry to the advisor's first answer"
                : "No answered threads with usable timestamps yet"
            }
          />
        </div>
      )}

      <ChartFrame
        title="Active accounts over time"
        subtitle="Each window is rolling and ends on the date under the point: DAU is that day, WAU the 7 days up to it, MAU the 30 days up to it."
        series={trendSeries}
        rows={data.series as unknown as Record<string, string | number>[]}
        columns={[
          { key: "date", label: "Date", format: (v) => longDate(String(v)) },
          { key: "dau", label: "DAU" },
          { key: "wau", label: "WAU" },
          { key: "mau", label: "MAU" },
          { key: "signups", label: "Signups" },
        ]}
      >
        <AreaTrendChart
          data={data.series as unknown as Record<string, string | number>[]}
          xKey="date"
          series={trendSeries}
          height={300}
          formatX={shortDate}
          formatTooltipX={longDate}
        />
      </ChartFrame>

      <div className="grid gap-5 xl:grid-cols-2">
        <ChartFrame
          title="New vs returning"
          subtitle="Of the accounts active on a day, how many were being seen for the very first time."
          series={mixSeries}
          rows={data.series as unknown as Record<string, string | number>[]}
          columns={[
            { key: "date", label: "Date", format: (v) => longDate(String(v)) },
            { key: "returning_active", label: "Returning" },
            { key: "new_active", label: "First time" },
            { key: "signups", label: "Signups" },
          ]}
        >
          <BarChart
            data={data.series as unknown as Record<string, string | number>[]}
            xKey="date"
            series={mixSeries}
            height={250}
            formatX={shortDate}
            formatTooltipX={longDate}
          />
        </ChartFrame>

        <ChartFrame
          title="Average daily actives by weekday"
          subtitle="Averaged across the selected range — the shape to schedule releases and sends around."
          rows={weekdayRows as unknown as Record<string, string | number>[]}
          columns={[
            { key: "label", label: "Weekday" },
            { key: "value", label: "Avg DAU" },
          ]}
        >
          <RankedBars
            rows={weekdayRows}
            formatValue={(n) => n.toFixed(1)}
            emptyLabel="No activity recorded in this range yet."
          />
        </ChartFrame>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <ChartFrame
          title="How recently each account was seen"
          subtitle={`Every one of the ${data.reach.total_users.toLocaleString()} registered accounts falls in exactly one band, including those never seen at all.`}
        >
          <BucketBar buckets={recencyBuckets} total={data.reach.total_users} />
        </ChartFrame>

        <ChartFrame
          title="Engagement depth"
          subtitle={`Distinct days each active account appeared in the last 30. Average ${data.depth.avg_active_days} days.`}
        >
          <BucketBar buckets={depthBuckets} />
        </ChartFrame>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Plans are a buyer concept — an advisor has no tier, so the panel
            would be a single "No plan" bar on that view. */}
        {!isAdvisorView && (
          <ChartFrame title="Active by plan" subtitle="Their current tier, if any.">
            <RankedBars rows={data.by_tier.map((t) => ({ label: t.key, value: t.active }))} />
          </ChartFrame>
        )}

        {/* Absent for a tenant admin: the API returns an empty list when the
            caller is scoped to a single organization, since one row would say
            nothing they don't already know. */}
        {data.by_org.length > 0 && (
          <ChartFrame title="Active by organization" subtitle="Across every tenant on the platform.">
            <RankedBars rows={data.by_org.map((o) => ({ label: o.key, value: o.active }))} />
          </ChartFrame>
        )}
      </div>

      {retention && (
        <ChartFrame
          title="Retention by signup cohort"
          subtitle="Each row is the week a group signed up. W0 is the share active in their first week, and the fall to the right is how many kept coming back."
        >
          <CohortGrid cohorts={retention.cohorts} weeks={retention.weeks} />
        </ChartFrame>
      )}

      {/* The advisor roster, which is what "most engaged" means on this view —
          every advisor with both halves of the picture on one row, so the
          generic engagement list below is redundant here and is hidden. */}
      {isAdvisorView && advisors && (
        <ChartFrame
          title="Advisor desk"
          subtitle={`Every BL advisor, with the work they carried between ${shortDate(data.range.start)} and ${shortDate(data.range.end)}. Sorted by days active.`}
        >
          {advisors.advisors.length === 0 ? (
            <p className="text-base text-muted-foreground">
              No BL advisors in this organization yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[70rem] text-left text-base">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-border/70">
                    <th className="px-2 py-2.5 font-semibold">Advisor</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Days active</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Listings</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Active</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Interests</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Replies</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Awaiting</th>
                    <th className="px-2 py-2.5 text-right font-semibold">1st reply</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Deals</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Shares</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Referred</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {advisors.advisors.map((a) => (
                    <tr key={a.user_id} className="border-b border-border/50 last:border-0">
                      <td className="px-2 py-2">
                        <div className="font-semibold text-foreground">{a.name}</div>
                        <div className="text-muted-foreground">{a.email}</div>
                      </td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums text-foreground">
                        {a.active_days}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-foreground">
                        {a.listings_managed.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {a.active_listings.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-foreground">
                        {a.interests_received.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {a.replies_sent.toLocaleString()}
                      </td>
                      {/* The one number worth acting on today, so it is the one
                          that gets a colour when it is non-zero. */}
                      <td
                        className={
                          a.awaiting_reply > 0
                            ? "px-2 py-2 text-right font-semibold tabular-nums text-amber-500"
                            : "px-2 py-2 text-right tabular-nums text-muted-foreground"
                        }
                      >
                        {a.awaiting_reply.toLocaleString()}
                      </td>
                      <td
                        className="px-2 py-2 text-right tabular-nums text-muted-foreground"
                        title={
                          a.avg_first_reply_hours != null
                            ? `Averaged over ${a.replied_threads} answered thread${a.replied_threads === 1 ? "" : "s"}`
                            : "No answered threads with usable timestamps"
                        }
                      >
                        {a.avg_first_reply_hours != null ? `${a.avg_first_reply_hours}h` : "—"}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {a.deals_created.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {a.shares_sent.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {a.buyers_referred.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {a.last_seen
                          ? new Date(a.last_seen).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            })
                          : "Never"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartFrame>
      )}

      {!isAdvisorView && (
      <ChartFrame
        title="Most engaged accounts"
        subtitle={`Ranked by distinct days active between ${shortDate(data.range.start)} and ${shortDate(data.range.end)}.`}
      >
        {data.top_users.length === 0 ? (
          <p className="text-base text-muted-foreground">No activity recorded in this range yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem] text-left text-base">
              <thead className="text-muted-foreground">
                <tr className="border-b border-border/70">
                  <th className="px-3 py-2.5 font-semibold">Account</th>
                  <th className="px-3 py-2.5 font-semibold">Role</th>
                  <th className="px-3 py-2.5 font-semibold">Plan</th>
                  <th className="px-3 py-2.5 font-semibold">Organization</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Days active</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/50 last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-foreground">{u.name}</div>
                      <div className="text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {u.role ? ROLE_LABELS[u.role] ?? u.role : "—"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{u.tier_name ?? "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{u.org_name ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-foreground">
                      {u.active_days}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {u.last_seen
                        ? new Date(u.last_seen).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartFrame>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label="Signups in range"
          value={data.series.reduce((a, s) => a + s.signups, 0).toLocaleString()}
          hint="New accounts created"
        />
        <StatTile
          label="Avg days active"
          value={String(data.depth.avg_active_days)}
          hint="Per active account, last 30 days"
        />
        <StatTile
          label="WAU / MAU"
          value={`${h.wau_mau}%`}
          hint="Share of the monthly audience seen in any given week"
        />
      </div>

      <p className="flex items-start gap-2.5 text-base leading-6 text-muted-foreground">
        <Gauge className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          An account counts as active on a day when it made an authenticated request. The
          signal is throttled server-side, so this measures days present rather than clicks
          or page views. All windows use UTC day boundaries.
        </span>
      </p>
    </PageShell>
  );
}
